/* eslint-disable @angular-eslint/template/click-events-have-key-events */
import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { RxIconModule } from '@nrwl/rx-icon';
import { parseLocalDateString } from '../../../../helpers/date-utils';
import {
  ScribeSuggestionIndicatorComponent,
  ScribeSuggestionIndicatorVariant,
} from '../../shared/scribe-suggestion-indicator/scribe-suggestion-indicator.component';
import { AmendmentFieldIndicatorComponent } from '../../shared/amendment-field-indicator/amendment-field-indicator.component';
import {
  SCRIBE_SUGGESTION_MARKER_PROPERTY,
  ScribeSuggestionMarker,
} from '../../../scribe-suggestion-marker.model';
import {
  clearScribeSuggestionMarker,
  isScribeSuggestionMarked,
} from '../../../scribe-suggestion-marker.util';

export type TableColumn = {
  title: string;
  name?: string;
  value?: string;
  type?: string;
  required?: boolean;
};

export type TableRow = {
  [key: string]: any;
  hasConflict?: boolean;
  conflictTooltip?: string;
  id?: string;
  isScribeSuggested?: ScribeSuggestionMarker;
};

@Component({
  selector: 'fb-emr-table',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDatepickerModule,
    MatNativeDateModule,
    RxIconModule,
    ScribeSuggestionIndicatorComponent,
    AmendmentFieldIndicatorComponent,
  ],
  template: `
    <div class="fb-component">
      <div class="fb__header">
        <div class="fb__title">{{ title }}</div>
        <div class="fb__note" *ngIf="populatedFrom">* populated from {{ populatedFrom }}</div>
      </div>

      <!-- Desktop Table -->
      <table class="fb__table fb__table--desktop">
        <thead>
          <tr>
            <th *ngFor="let col of columns">
              <span class="fb__th-content">
                <span>{{ col.title }}</span>
              </span>
            </th>
            <th class="fb__action-cell" *ngIf="!isPrinting"></th>
          </tr>
        </thead>
        <tbody>
          <ng-container>
            <tr
              *ngFor="let row of renderedValues; let i = index; trackBy: trackByIndex"
              [class.fb__row--empty]="i === 0 && isRowEmpty(row)"
              (click)="onEmptyRowClick(i, $event)"
            >
              <td *ngFor="let col of columns; let colIndex = index">
                <div class="fb__cell-content">
                  <ng-container [ngSwitch]="col.type">
                    <!-- Date input -->
                    <ng-container *ngSwitchCase="'date'">
                      <div
                        class="fb__date-wrap"
                        [class.fb__date-wrap--scribe]="isCellScribeSuggestionMarked(row, col)"
                        [class.fb__date-wrap--conflict]="
                          !isPreview &&
                          (row.hasConflict?.[col.name] || row.hasConflict?.[col.title])
                        "
                      >
                        <input
                          class="fb__input"
                          [ngClass]="{
                            'has-conflict':
                              !isPreview &&
                              (row.hasConflict?.[col.name] || row.hasConflict?.[col.title]),
                          }"
                          type="text"
                          placeholder="MM/DD/YYYY"
                          [matDatepicker]="picker"
                          [(ngModel)]="row[col.title]"
                          (input)="onDateInputForRow(row, col, $event)"
                          (ngModelChange)="onCellChangedForRow(row, col, $event)"
                          [disabled]="disabled || readonly"
                          [attr.aria-label]="col.title"
                          (focus)="picker.open()"
                        />
                        <button
                          type="button"
                          class="fb-btn fb-btn--icon fb-btn--calendar"
                          (click)="picker.open()"
                          [disabled]="disabled || readonly"
                        >
                          <rx-icon name="icon-calendar2"></rx-icon>
                        </button>
                        <rx-icon
                          *ngIf="
                            !isPreview &&
                            (row.hasConflict?.[col.name] || row.hasConflict?.[col.title])
                          "
                          name="icon-warning-circle"
                          class="conflict-warning-icon"
                          [title]="
                            row.conflictTooltip?.[col.name] ||
                            row.conflictTooltip?.[col.title] ||
                            row.conflictTooltip ||
                            'Conflict detected'
                          "
                        ></rx-icon>
                        <mat-datepicker #picker></mat-datepicker>
                        <fb-scribe-suggestion-indicator
                          class="fb__cell-scribe-indicator"
                          [target]="row"
                          [fieldKey]="getColumnKey(col)"
                          variant="compact"
                        ></fb-scribe-suggestion-indicator>
                      </div>
                    </ng-container>

                    <!-- Default text input -->
                    <ng-container *ngSwitchDefault>
                      <div
                        class="fb__input-wrapper"
                        [class.fb__input-wrapper--scribe]="isCellScribeSuggestionMarked(row, col)"
                        [class.fb__input-wrapper--conflict]="
                          !isPreview &&
                          (row.hasConflict?.[col.name] || row.hasConflict?.[col.title])
                        "
                      >
                        <input
                          class="fb__input"
                          [ngClass]="{
                            'has-conflict':
                              !isPreview &&
                              (row.hasConflict?.[col.name] || row.hasConflict?.[col.title]),
                            'fb__input--readonly': isNameColumn(col.title),
                          }"
                          type="text"
                          [(ngModel)]="row[col.title]"
                          (ngModelChange)="onCellChangedForRow(row, col, $event)"
                          [placeholder]="'Enter ' + col.title.toLowerCase()"
                          [disabled]="disabled || readonly || isNameColumn(col.title)"
                          [attr.aria-label]="col.name"
                        />
                        <rx-icon
                          *ngIf="
                            !isPreview &&
                            (row.hasConflict?.[col.name] || row.hasConflict?.[col.title])
                          "
                          name="icon-warning-circle"
                          class="conflict-warning-icon"
                          [title]="
                            row.conflictTooltip?.[col.name] ||
                            row.conflictTooltip?.[col.title] ||
                            row.conflictTooltip ||
                            'Conflict detected'
                          "
                        ></rx-icon>
                        <fb-scribe-suggestion-indicator
                          class="fb__cell-scribe-indicator"
                          [target]="row"
                          [fieldKey]="getColumnKey(col)"
                          variant="compact"
                        ></fb-scribe-suggestion-indicator>
                      </div>
                    </ng-container>
                  </ng-container>
                  <fb-amendment-field-indicator
                    [fieldKey]="amendmentCategory + ':r' + i + ':' + getColumnKey(col)"
                  />
                </div>
              </td>
              <td class="fb__action-cell" *ngIf="!isPrinting">
                <button
                  type="button"
                  class="fb-btn fb-btn--icon fb-btn--remove"
                  (click)="removeRow(i)"
                  aria-label="Remove row"
                >
                  <rx-icon name="icon-close"></rx-icon>
                </button>
              </td>
            </tr>
          </ng-container>
        </tbody>
      </table>

      <!-- Mobile Table (for medications) -->
      <div class="fb__table--mobile" *ngIf="title === 'List any Medications'">
        <div
          class="fb__mobile-row"
          *ngFor="let row of renderedValues; let i = index; trackBy: trackByIndex"
          [class.fb__row--empty]="i === 0 && isRowEmpty(row)"
          (click)="onEmptyRowClick(i, $event)"
        >
          <!-- Dates Section -->
          <div class="fb__mobile-dates-section">
            <div class="fb__mobile-field-group">
              <label class="fb__mobile-label">
                <span class="fb__mobile-label-content">
                  <span>Start:</span>
                  <fb-scribe-suggestion-indicator
                    [target]="row"
                    fieldKey="Date"
                    [variant]="scribeIndicatorVariant"
                  ></fb-scribe-suggestion-indicator>
                  <fb-amendment-field-indicator
                    [fieldKey]="amendmentCategory + ':r' + i + ':Date'"
                  />
                </span>
              </label>
              <div class="fb__date-wrap">
                <input
                  class="fb__input fb__input--mobile"
                  type="text"
                  placeholder="MM/DD/YYYY"
                  [matDatepicker]="startPicker"
                  [(ngModel)]="row['Date']"
                  (input)="onMobileDateInput(i, 'Date', $event)"
                  (ngModelChange)="onMobileFieldChanged(i, 'Date', $event)"
                  [disabled]="disabled || readonly"
                  (click)="startPicker.open()"
                />
                <button
                  type="button"
                  class="fb-btn fb-btn--icon fb-btn--calendar"
                  (click)="startPicker.open()"
                  [disabled]="disabled || readonly"
                >
                  <rx-icon name="icon-calendar2"></rx-icon>
                </button>
                <mat-datepicker #startPicker></mat-datepicker>
              </div>
            </div>
            <div class="fb__mobile-field-group">
              <label class="fb__mobile-label">
                <span class="fb__mobile-label-content">
                  <span>Stop:</span>
                  <fb-scribe-suggestion-indicator
                    [target]="row"
                    fieldKey="Stop"
                    [variant]="scribeIndicatorVariant"
                  ></fb-scribe-suggestion-indicator>
                  <fb-amendment-field-indicator
                    [fieldKey]="amendmentCategory + ':r' + i + ':Stop'"
                  />
                </span>
              </label>
              <div class="fb__date-wrap">
                <input
                  class="fb__input fb__input--mobile"
                  type="text"
                  placeholder="MM/DD/YYYY"
                  [matDatepicker]="stopPicker"
                  [(ngModel)]="row['Stop']"
                  (input)="onMobileDateInput(i, 'Stop', $event)"
                  (ngModelChange)="onMobileFieldChanged(i, 'Stop', $event)"
                  [disabled]="disabled || readonly"
                  (click)="stopPicker.open()"
                />
                <button
                  type="button"
                  class="fb-btn fb-btn--icon fb-btn--calendar"
                  (click)="stopPicker.open()"
                  [disabled]="disabled || readonly"
                >
                  <rx-icon name="icon-calendar2"></rx-icon>
                </button>
                <mat-datepicker #stopPicker></mat-datepicker>
              </div>
            </div>
          </div>

          <!-- Medication Details Section -->
          <div class="fb__mobile-details-section">
            <div class="fb__mobile-field-group fb__mobile-field-group--medication">
              <label class="fb__mobile-label">
                <span class="fb__mobile-label-content">
                  <span>Medication:</span>
                  <fb-scribe-suggestion-indicator
                    [target]="row"
                    fieldKey="Medication"
                    [variant]="scribeIndicatorVariant"
                  ></fb-scribe-suggestion-indicator>
                  <fb-amendment-field-indicator
                    [fieldKey]="amendmentCategory + ':r' + i + ':Medication'"
                  />
                </span>
              </label>
              <input
                class="fb__input fb__input--mobile fb__input--readonly"
                type="text"
                [(ngModel)]="row['Medication']"
                (ngModelChange)="onMobileFieldChanged(i, 'Medication', $event)"
                placeholder="Enter medication"
                [disabled]="disabled || readonly || true"
              />
            </div>
            <div class="fb__mobile-field-group fb__mobile-field-group--freq">
              <label class="fb__mobile-label">
                <span class="fb__mobile-label-content">
                  <span>Freq:</span>
                  <fb-scribe-suggestion-indicator
                    [target]="row"
                    fieldKey="Freq"
                    [variant]="scribeIndicatorVariant"
                  ></fb-scribe-suggestion-indicator>
                  <fb-amendment-field-indicator
                    [fieldKey]="amendmentCategory + ':r' + i + ':Freq'"
                  />
                </span>
              </label>
              <input
                class="fb__input fb__input--mobile"
                type="text"
                [(ngModel)]="row['Freq']"
                (ngModelChange)="onMobileFieldChanged(i, 'Freq', $event)"
                placeholder="1x"
                [disabled]="disabled || readonly"
              />
            </div>
            <div class="fb__mobile-field-group fb__mobile-field-group--dosage">
              <label class="fb__mobile-label">
                <span class="fb__mobile-label-content">
                  <span>Dosage:</span>
                  <fb-scribe-suggestion-indicator
                    [target]="row"
                    fieldKey="Dosage"
                    [variant]="scribeIndicatorVariant"
                  ></fb-scribe-suggestion-indicator>
                  <fb-amendment-field-indicator
                    [fieldKey]="amendmentCategory + ':r' + i + ':Dosage'"
                  />
                </span>
              </label>
              <input
                class="fb__input fb__input--mobile"
                type="text"
                [(ngModel)]="row['Dosage']"
                (ngModelChange)="onMobileFieldChanged(i, 'Dosage', $event)"
                placeholder="mg"
                [disabled]="disabled || readonly"
              />
            </div>
            <button
              type="button"
              class="fb-btn fb-btn--icon fb-btn--remove fb-btn--mobile"
              *ngIf="!isPrinting"
              (click)="removeRow(i)"
              aria-label="Remove row"
            >
              <rx-icon name="icon-close"></rx-icon>
            </button>
          </div>
        </div>
      </div>

      <!-- Add row button -->
      <button
        type="button"
        class="fb-btn fb-btn--add"
        (click)="addRow()"
        [disabled]="disabled || readonly"
        *ngIf="!isPrinting"
      >
        <span class="fb-btn__icon"
          ><rx-icon name="icon-circle-plus-2" [size]="16" class="blue"></rx-icon
        ></span>
        <span>{{ addTitle || 'Add Row' }}</span>
      </button>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        font-family: Roboto, Arial, sans-serif;
      }

      /* Component Container */
      .fb-component {
        width: 100%;
        margin-bottom: 20px;
      }

      /* Header */
      .fb__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 17px;
      }

      .fb__title {
        font-family: Roboto, Arial, sans-serif;
        font-weight: 500;
        font-size: 14px;
        line-height: 100%;
        color: #000;
        page-break-after: avoid;
        break-after: avoid;
      }

      .fb__note {
        font-size: 12px;
        color: #6b7280;
        margin-left: 16px;
      }

      /* Table */
      .fb__table {
        width: 100%;
        border-collapse: collapse;
        margin-bottom: 0;
        border-radius: 8px;
        overflow: hidden;
      }

      .fb__table th {
        text-align: left;
        padding: 19px 12px;
        font-weight: 400;
        font-size: 14px;
        height: 54px;
        background: #f1f1f3;
        border-bottom: 1px solid #e5e7eb;
        box-sizing: border-box;
      }

      .fb__th-content {
        position: relative;
        display: inline-flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 4px 8px;
        max-width: 100%;
        min-width: 0;
      }

      .fb__th-content > :first-child {
        min-width: 0;
        overflow-wrap: anywhere;
      }

      .fb__table td {
        padding: 5px;
        border-bottom: 1px solid #e5e7eb;
        background: #fff;
      }

      .fb__row--empty {
        cursor: pointer;
      }

      .fb__row--empty td {
        position: relative;
      }

      .fb__row--empty .fb__input,
      .fb__row--empty .fb-btn {
        pointer-events: none;
        opacity: 0.7;
      }

      .fb__row--empty:hover {
        background: rgba(0, 97, 253, 0.03);
      }

      .fb__action-cell {
        width: 40px;
        text-align: center;
      }

      .fb__placeholder-cell {
        color: #a0a4ae;
        text-align: center;
      }

      /* Inputs */
      .fb__input {
        height: 40px;
        width: 100%;
        padding: 0 12px;
        border: 1px solid #d2d6df;
        border-radius: 6px;
        background: #fff;
        outline: none;
        font-size: 14px;
        line-height: 20px;
      }

      .fb__input::placeholder {
        color: #70737d;
      }

      .fb__input:focus {
        border-color: #0061fd;
        box-shadow: 0 0 0 2px rgba(0, 97, 253, 0.15);
      }

      .fb__input.has-conflict {
        border-color: #f1a23b !important;
      }

      .fb__input--readonly {
        text-overflow: ellipsis;
        overflow: hidden !important;
        border: 0;
        padding: 0;
      }

      .fb__cell-content {
        display: flex;
        align-items: center;
      }

      .fb__cell-content > .fb__input-wrapper,
      .fb__cell-content > .fb__date-wrap {
        flex: 1 1 0;
        min-width: 0;
      }

      .fb__cell-content > fb-amendment-field-indicator {
        flex: 0 0 auto;
        margin-left: 6px;
      }

      .fb__input-wrapper {
        position: relative;
        width: 100%;
      }

      .fb__cell-scribe-indicator {
        position: absolute;
        top: 50%;
        right: 8px;
        z-index: 2;
        transform: translateY(-50%);
      }

      .fb__input-wrapper--scribe .fb__input {
        padding-right: 48px;
      }

      .fb__input-wrapper--scribe.fb__input-wrapper--conflict .fb__input {
        padding-right: 74px;
      }

      .fb__input-wrapper--scribe .conflict-warning-icon {
        right: 46px;
      }

      .conflict-warning-icon {
        position: absolute;
        right: 10px;
        top: 50%;
        transform: translateY(-50%);
        color: #f1a23b;
        z-index: 1;
        pointer-events: auto;
        cursor: help;

        ::ng-deep svg path {
          fill: #f1a23b;
        }

        ::ng-deep svg {
          width: 20px;
          height: 20px;
        }
      }

      .fb__date-wrap .conflict-warning-icon {
        right: 45px;
      }

      /* Date input with calendar */
      .fb__date-wrap {
        position: relative;
      }

      .fb__date-wrap .fb__cell-scribe-indicator {
        right: 48px;
      }

      .fb__date-wrap--scribe .fb__input {
        padding-right: 88px;
      }

      .fb__date-wrap--scribe.fb__date-wrap--conflict .fb__input {
        padding-right: 112px;
      }

      .fb__date-wrap--scribe .conflict-warning-icon {
        right: 84px;
      }

      .fb__date-wrap .fb-btn--calendar {
        position: absolute;
        right: 8px;
        top: 50%;
        transform: translateY(-50%);
      }

      /* Buttons */
      .fb-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: transparent;
        border: none;
        cursor: pointer;
        padding: 0;
      }

      .fb-btn--icon {
        width: 40px;
        height: 40px;
        ::ng-deep {
          .mat-icon svg path {
            fill: transparent;
          }
        }
      }

      .fb-btn--icon:hover {
        background: rgba(0, 97, 253, 0.06);
        border-radius: 50%;
      }

      .fb-btn--add {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        margin-top: 8px;
        padding: 4px 0;
        font-size: 14px;
        line-height: 20px;
        color: #0b5bff;
        span {
          font-size: 14px;
        }
      }

      .fb-btn--remove {
        ::ng-deep {
          .mat-icon svg path {
            fill: #777;
          }
        }
      }

      .fb-btn__icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }

      @media print {
        .fb__input::placeholder {
          color: transparent !important;
        }

        .fb__date-wrap .fb-btn--calendar,
        .conflict-warning-icon {
          display: none !important;
        }

        .fb__date-wrap .fb__input {
          padding-right: 12px;
        }

        .fb__date-wrap .fb__cell-scribe-indicator {
          right: 8px;
        }

        .fb__date-wrap--scribe .fb__input,
        .fb__date-wrap--scribe.fb__date-wrap--conflict .fb__input {
          padding-right: 48px;
        }
      }

      /* Mobile Styles */
      .fb__table--desktop {
        display: table;
      }

      .fb__table--mobile {
        display: none;
      }

      @media (max-width: 640px) {
        .fb__table--desktop {
          display: none;
        }

        .fb__table--mobile {
          display: block;
        }

        .fb__mobile-row {
          background: #fff;
          border-bottom: 1px solid #e5e7eb;
          border-radius: 0;
          margin-bottom: 12px;
          padding: 5px 0 20px;
        }

        /* Empty row styling for mobile */
        .fb__table--mobile .fb__row--empty {
          cursor: pointer;
          border-bottom: 1px dashed #d2d6df;
          background: #f9fafb;
        }

        .fb__table--mobile .fb__row--empty:hover {
          background: #f3f4f6;
          border-bottom-color: #9ca3af;
        }

        /* Dates Section */
        .fb__mobile-dates-section {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          padding-bottom: 12px;
          margin-bottom: 12px;
          border-bottom: 1px solid #f1f1f3;
        }

        /* Details Section */
        .fb__mobile-details-section {
          display: grid;
          grid-template-columns: 1fr auto auto 32px;
          gap: 12px;
          align-items: end;
        }

        /* Field Groups */
        .fb__mobile-field-group {
          display: flex;
          flex-direction: column;
          gap: 4px;
          break-inside: avoid;
        }

        /* Date wrap for mobile */
        .fb__mobile-field-group .fb__date-wrap {
          position: relative;
          width: 100%;
        }

        .fb__mobile-field-group .fb__date-wrap .fb__input {
          padding-right: 40px;
        }

        .fb__mobile-field-group .fb__date-wrap .fb-btn--calendar {
          position: absolute;
          right: 0;
          top: 50%;
          transform: translateY(-50%);
          width: 32px;
          height: 32px;
        }

        .fb__mobile-field-group--medication {
          grid-column: 1;
        }

        .fb__mobile-field-group--freq {
          grid-column: 2;
          width: 70px;
        }

        .fb__mobile-field-group--dosage {
          grid-column: 3;
          width: 90px;
        }

        /* Labels */
        .fb__mobile-label {
          font-weight: 500;
          font-size: 11px;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .fb__mobile-label-content {
          position: relative;
          display: inline-flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 4px 8px;
          max-width: 100%;
          min-width: 0;
        }

        .fb__mobile-label-content > :first-child {
          min-width: 0;
          overflow-wrap: anywhere;
        }

        /* Values */
        .fb__mobile-value {
          font-size: 14px;
          color: #000;
          min-height: 20px;
        }

        /* Inputs */
        .fb__input--mobile {
          height: 36px;
          font-size: 14px;
          padding: 0 8px;
          width: 100%;
        }

        .fb-btn--mobile {
          width: 32px;
          height: 32px;
          grid-column: 4;
          justify-self: end;
        }

        .fb-btn--mobile ::ng-deep .mat-icon {
          width: 16px;
          height: 16px;
        }

        .fb-btn--mobile:hover {
          background: rgba(255, 0, 0, 0.06);
        }

        .fb-btn--add {
          width: 100%;
          justify-content: center;
          padding: 10px;
          background: #f1f1f3;
          border-radius: 8px;
          margin-top: 12px;
        }
      }
    `,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FbEmrTableComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FbEmrTableComponent implements ControlValueAccessor {
  @Input() title = '';
  /**
   * Name of the medical history category this component edits, for the amendment indicator.
   */
  @Input() amendmentCategory = '';
  @Input() addTitle = '';
  @Input() populatedFrom: string = '';
  @Input() readonly = false;
  @Input() isDesign = false;
  @Input() isPrinting = false;
  @Input() showEmptyRowsInPrint = false;
  @Input() isPreview = false;
  @Input() scribeIndicatorVariant: ScribeSuggestionIndicatorVariant = 'full';

  private isDeleting = false;

  constructor(private cd: ChangeDetectorRef) {}

  @Input() set values(values: TableRow[]) {
    if (!Array.isArray(values)) {
      this._values = [];
    } else {
      this._values = values;
    }

    if (!this.isPrinting && this._values.length === 0) {
      this._values = [this.createEmptyRow()];
    }

    this.valuesChange.emit([...this._values]);
    this.onChange(this._values);
    this.onTouched();
  }
  get values(): TableRow[] {
    return this._values;
  }

  get renderedValues(): TableRow[] {
    if (!this.isPrinting) {
      return this._values;
    }

    if (this.showEmptyRowsInPrint) {
      return this._values;
    }

    return this._values.filter((row) => !this.isRowEmpty(row));
  }

  @Input() set columns(value: TableColumn[]) {
    this._columns = value || [];

    // if (!this.isDesign && !this.isPrinting && this._values.length === 0) {
    //     this._values = [this.createEmptyRow()];
    // }

    // this.valuesChange.emit([...this._values]);
    // this.onChange(this._values);
  }
  get columns(): TableColumn[] {
    return this._columns;
  }

  @Output() valuesChange = new EventEmitter<TableRow[]>();
  @Output() addClick = new EventEmitter<void>();
  @Output() emptyRowClick = new EventEmitter<void>();

  private _columns: TableColumn[] = [];
  private _values: TableRow[] = [];
  disabled = false;

  // ControlValueAccessor
  onChange: (value: TableRow[]) => void = () => {};
  onTouched: () => void = () => {};

  writeValue(value: TableRow[] | null): void {
    if (!value || !Array.isArray(value)) {
      this._values = this.isPrinting ? [] : [this.createEmptyRow()];
      return;
    }

    this._values = value;

    if (!this.isPrinting && this._values.length === 0) {
      this._values = [this.createEmptyRow()];
    }
  }

  registerOnChange(fn: (value: TableRow[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // Actions
  addRow(prefill?: TableRow) {
    if (this.addClick.observers.length > 0) {
      this.addClick.emit();
      return;
    }

    const emptyRow = prefill || this.createEmptyRow();
    this._values = [...this._values, emptyRow];
    this.onValuesChanged();
  }

  removeRow(index: number) {
    this.isDeleting = true;
    if (this._values.length === 1) {
      this._values = [this.createEmptyRow()];
    } else {
      this._values = this._values.filter((_, i) => i !== index);
    }
    this.onValuesChanged();
    // Reset the flag after a short delay to allow the DOM to update
    setTimeout(() => {
      this.isDeleting = false;
      this.cd.detectChanges();
    }, 100);
  }

  onCellChanged(i: number, col: TableColumn, value: any) {
    const coerced = col.type === 'date' ? this.parseDate(value) : (value ?? '');
    const target = this._values[i];
    if (target) {
      clearScribeSuggestionMarker(target, col.title);
    }
    this._values = this._values.map((row, idx) =>
      idx === i ? { ...row, [col.title]: coerced } : row,
    );
    this.onValuesChanged();
  }

  onCellChangedForRow(row: TableRow, col: TableColumn, value: any) {
    const nextValue = col.type === 'date' ? this.parseDate(value) : (value ?? '');
    clearScribeSuggestionMarker(row, col.title);
    this._values = this._values.map((currentRow) =>
      currentRow === row ? { ...currentRow, [col.title]: nextValue } : currentRow,
    );
    this.onValuesChanged();
  }

  onDateInputForRow(row: TableRow, col: TableColumn, event: Event) {
    const rawValue = (event.target as HTMLInputElement | null)?.value || '';
    if (rawValue.trim() !== '') {
      return;
    }

    clearScribeSuggestionMarker(row, col.title);
    this._values = this._values.map((currentRow) =>
      currentRow === row ? { ...currentRow, [col.title]: '' } : currentRow,
    );
    this.onValuesChanged();
  }

  onValuesChanged() {
    const next = [...this._values];
    this.valuesChange.emit(next);
    this.onChange(next);
    this.onTouched();
  }

  trackByIndex(index: number) {
    return index;
  }

  onEmptyRowClick(rowIndex: number, event: MouseEvent) {
    // Don't trigger dialog if we're in the middle of deleting
    if (this.isDeleting) {
      return;
    }

    if (rowIndex === 0 && this.isRowEmpty(this._values[rowIndex])) {
      event.stopPropagation();

      if (this.emptyRowClick.observers.length > 0) {
        this.emptyRowClick.emit();
      } else if (this.addClick.observers.length > 0) {
        this.addClick.emit();
      }
    }
  }

  isRowEmpty(row: TableRow): boolean {
    if (!row || typeof row !== 'object') return true;
    return Object.entries(row)
      .filter(([key]) => key !== SCRIBE_SUGGESTION_MARKER_PROPERTY)
      .every(([, value]) => !value || value === '');
  }

  isCellScribeSuggestionMarked(row: TableRow, col: TableColumn): boolean {
    return isScribeSuggestionMarked(row, this.getColumnKey(col));
  }

  getColumnKey(col: TableColumn): string {
    return String(col.title || col.name || '');
  }

  replaceEmptyRowWithData(data: TableRow) {
    // If the first row is empty, replace it with the new data
    if (this._values.length > 0 && this.isRowEmpty(this._values[0])) {
      this._values[0] = data;
    } else {
      // Otherwise add as a new row
      this._values = [...this._values, data];
    }
    this.onValuesChanged();
    // Force immediate change detection
    this.cd.markForCheck();
    this.cd.detectChanges();
  }

  private createEmptyRow(): TableRow {
    const row: TableRow = {};
    this._columns.forEach((col) => {
      const columnKey = col.title || col.name || '';
      const value = col.value ?? '';
      row[columnKey] = col.type === 'date' ? this.parseDate(value) : value;
    });
    return row;
  }

  private parseDate(value: any): string {
    if (!value) return '';

    if (typeof value === 'string') {
      const trimmedValue = value.trim();

      if (!trimmedValue) {
        return '';
      }

      const parsedDate = parseLocalDateString(trimmedValue);
      const match = /^(\d{4})-\d{2}-\d{2}$/.exec(parsedDate);

      return match && Number(match[1]) >= 1910 ? parsedDate : value;
    }

    const parsedDate = parseLocalDateString(value);
    const match = /^(\d{4})-\d{2}-\d{2}$/.exec(parsedDate);
    return match && Number(match[1]) >= 1910 ? parsedDate : '';
  }

  isNameColumn(title: string): boolean {
    return (
      title === 'Medication' || title === 'Condition' || title === 'Allergy' || title === 'Surgery'
    );
  }

  formatDateForMobile(dateStr: string): string {
    if (!dateStr) return 'N/A';
    // If it's already a date object or ISO string, format it
    try {
      const date = new Date(dateStr);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        });
      }
    } catch (e) {
      // Fall through to return original string
    }
    return dateStr || 'N/A';
  }

  onMobileFieldChanged(index: number, fieldName: string, value: any) {
    const nextValue =
      fieldName === 'Date' || fieldName === 'Stop' ? this.parseDate(value) : (value ?? '');

    const target = this._values[index];
    if (target) {
      clearScribeSuggestionMarker(target, fieldName);
    }
    this._values = this._values.map((row, idx) =>
      idx === index ? { ...row, [fieldName]: nextValue } : row,
    );
    this.onValuesChanged();
  }

  onMobileDateInput(index: number, fieldName: string, event: Event) {
    const rawValue = (event.target as HTMLInputElement | null)?.value || '';
    if (rawValue.trim() !== '') {
      return;
    }

    const target = this._values[index];
    if (target) {
      clearScribeSuggestionMarker(target, fieldName);
    }
    this._values = this._values.map((row, idx) =>
      idx === index ? { ...row, [fieldName]: '' } : row,
    );
    this.onValuesChanged();
  }

  onMobileRowClick(index: number, row: TableRow) {
    // Check if this is an empty row first
    if (index === 0 && this.isRowEmpty(row)) {
      // For empty rows, trigger add dialog
      if (this.emptyRowClick.observers.length > 0) {
        this.emptyRowClick.emit();
      } else if (this.addClick.observers.length > 0) {
        this.addClick.emit();
      }
    } else {
      // For existing rows with data, use the same addClick event
      // This triggers the parent's onAddItem which should handle edit dialogs
      this.addClick.emit();
    }
  }
}
